import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, IconButton, Alert, Button, CircularProgress } from '@mui/material';
import { Menu as MenuIcon, ViewInAr as View3DIcon, Close as CloseIcon, VideoLibrary as VideoIcon, Stop as StopIcon } from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import { loadTarsiusWasm, getWasmModule, isWasmLoaded } from './wasm/wasmLoader';
import VideoProcessingPage from './pages/VideoProcessingPage';
import { getTestSet, type TestSetV2Response } from './api/videoApi';
import { parseExr } from './utils/exrParser';
import { VideoFrameDecoder } from './video/VideoFrameDecoder';
import { VideoTextureBridge } from './video/VideoTextureBridge';
import LocationSearchBar from './components/LocationSearchBar';
import TimeOfDayControl from './components/TimeOfDayControl';
import FpsCounter from './components/FpsCounter';
import TileMetricsOverlay from './components/TileMetricsOverlay';
import LayerPanel from './components/LayerPanel';
import TimeSlider from './components/TimeSlider';
import RasterUploadPanel from './components/RasterUploadPanel';
import HazardTooltip, { TooltipData } from './components/HazardTooltip';
import { useLayerManager, useRasterLayerManager } from './hooks/useLayerManager';
import { TemporalProvider } from './context/TemporalController';
import { GlassPanel } from 'liquidglass-react';
import { SceneGlassProvider } from './components/SceneGlassProvider';
import './App.css';

/**
 * RasterLayerManagerActivator — activates the raster tile system inside TemporalProvider.
 *
 * useRasterLayerManager() consumes TemporalContext, so it must be called inside a
 * TemporalProvider descendant. This thin component bridges that constraint.
 * Renders nothing — purely a hook activation site.
 */
function RasterLayerManagerActivator() {
  useRasterLayerManager();
  return null;
}

// Simple theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

type ViewMode = 'renderer' | 'video-processing';

function toDMS(deg: number, isLat: boolean): string {
  const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const m = Math.floor((abs - d) * 60);
  const s = ((abs - d) * 60 - m) * 60;
  return `${d}\u00b0${String(m).padStart(2, '0')}'${s.toFixed(2)}" ${dir}`;
}

// Isolated component so camera state updates don't re-render parent/GlassPanels
function CameraCoordinates({ rendererRef }: { rendererRef: React.MutableRefObject<any> }) {
  const [loc, setLoc] = useState<{lon: number; lat: number; alt: number; heading: number; pitch: number} | null>(null);
  const [copied, setCopied] = useState(false);
  const lastUpdate = React.useRef(0);

  React.useEffect(() => {
    let rafId: number;
    const poll = () => {
      const now = performance.now();
      if (now - lastUpdate.current > 250 && rendererRef.current) {
        try {
          const c = rendererRef.current.getCameraLocation();
          if (c) setLoc({ lon: c.lon, lat: c.lat, alt: c.alt, heading: c.heading, pitch: c.pitch });
        } catch {}
        lastUpdate.current = now;
      }
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, [rendererRef]);

  const handleCopy = useCallback(() => {
    if (loc) {
      navigator.clipboard.writeText(`(${loc.lon}, ${loc.lat}, ${loc.alt}, ${loc.heading.toFixed(1)}, ${loc.pitch.toFixed(1)})`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [loc]);

  if (!loc) return null;

  return (
    <Box
      onClick={handleCopy}
      sx={{
        position: 'absolute',
        bottom: 20,
        right: 20,
        zIndex: 10,
        cursor: 'pointer',
      }}
    >
      <GlassPanel
        cornerRadius={12}
        style={{ padding: '8px 16px' }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '0.75rem',
            fontWeight: 500,
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
          }}
        >
          {copied
            ? 'Copied!'
            : `${toDMS(loc.lat, true)}  ${toDMS(loc.lon, false)}  ${loc.alt > 1000 ? `${Math.round(loc.alt)}m` : `${loc.alt.toFixed(1)}m`}`
          }
        </Typography>
      </GlassPanel>
    </Box>
  );
}

// URL parameter ?noGlass=1 disables glass engine for A/B benchmarking
const noGlass = new URLSearchParams(window.location.search).has('noGlass');
// URL parameter ?diag=1 enables per-frame timing diagnostics in console
const diagMode = new URLSearchParams(window.location.search).has('diag');

function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('renderer');
  const [renderer, setRenderer] = useState<any>(null);

  // Layer management hook — wires pollers to renderer.pushMarkerData
  const { layers, onToggle, onOpacityChange } = useLayerManager();

  // Tooltip state for marker click (HAZ-05)
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copyrightText, setCopyrightText] = useState<string>('');
  const [showMetricsOverlay, setShowMetricsOverlay] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [availablePipelines, setAvailablePipelines] = useState<string[]>([]);
  const [activePipeline, setActivePipeline] = useState<string | null>(null);
  const [loadingPipeline, setLoadingPipeline] = useState<string | null>(null);
  const depthProgressRef = useRef<{ loaded: number; total: number } | null>(null);
  const depthProgressElRef = useRef<HTMLSpanElement>(null);
  const depthAbortRef = useRef(0);
  const testSetDataRef = useRef<TestSetV2Response | null>(null);
  const rendererInstanceRef = React.useRef<any>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [gpuDevice, setGpuDevice] = useState<GPUDevice | null>(null);
  const [sceneTexture, setSceneTexture] = useState<GPUTexture | null>(null);
  const wasmModuleRef = React.useRef<any>(null);
  const videoDecoderRef = useRef<VideoFrameDecoder | null>(null);
  const videoBridgeRef = useRef<VideoTextureBridge | null>(null);


  // Fetch available pipelines from test-set API on mount
  useEffect(() => {
    getTestSet()
      .then((ts) => {
        if (ts && 'pipelines' in ts) {
          testSetDataRef.current = ts as TestSetV2Response;
          setAvailablePipelines(Object.keys(ts.pipelines));
        }
      })
      .catch(() => {});
  }, []);

  const cleanupVideoResources = useCallback(() => {
    if (videoBridgeRef.current) {
      videoBridgeRef.current.destroy();
      videoBridgeRef.current = null;
    }
    if (videoDecoderRef.current) {
      videoDecoderRef.current.destroy();
      videoDecoderRef.current = null;
    }
  }, []);

  // Handle pipeline button click: load camera data + depth maps for one pipeline
  const handlePipelineClick = useCallback(async (name: string) => {
    const r = (window as any)._scRenderer;
    if (!r || !r.clearAllCameraData) return;

    // Abort any in-progress depth loading
    const abortToken = ++depthAbortRef.current;
    depthProgressRef.current = null;
    if (depthProgressElRef.current) depthProgressElRef.current.textContent = '';

    if (name === activePipeline) {
      r.clearAllCameraData();
      r.setActivePipeline(-1);
      setActivePipeline(null);
      cleanupVideoResources();
      return;
    }
    cleanupVideoResources();
    setLoadingPipeline(name);
    r.clearAllCameraData();
    r.setActivePipeline(-1);
    setActivePipeline(null);
    const tsData = testSetDataRef.current;
    if (!tsData) { setLoadingPipeline(null); return; }
    const pipelineData = tsData.pipelines[name];
    if (!pipelineData?.camera_data_url) { setLoadingPipeline(null); return; }

    // Fly camera to NYC test position (same as previous default start position)
    if (r.flyToWithOrientation) {
      r.flyToWithOrientation(-74.006, 40.7125, 30.0, 0.0, -15.0);
    } else if (r.flyTo) {
      r.flyTo(-74.006, 40.7125, 30.0);
    }

    // Phase 1: Load camera data (frustums start animating immediately)
    let cameraFrameCount = 0;
    try {
      const resp = await fetch(pipelineData.camera_data_url);
      const json = await resp.text();
      r.loadCameraData(json, name);
      r.setActivePipeline(0);
      setActivePipeline(name);
      // Parse camera frame count for video-to-camera frame mapping
      try { cameraFrameCount = JSON.parse(json).frames?.length ?? 0; } catch (_) {}
    } catch (err) {
      console.warn(`Failed to load pipeline ${name}:`, err);
      setLoadingPipeline(null);
      return;
    }
    setLoadingPipeline(null);

    // Phase 1.5: Video decode + bridge wiring
    const videoUrl = tsData.video_url;
    if (videoUrl && gpuDevice) {
      try {
        const decoder = new VideoFrameDecoder(gpuDevice);
        videoDecoderRef.current = decoder;
        await decoder.load(videoUrl);

        // Race guard
        if (videoDecoderRef.current !== decoder || depthAbortRef.current !== abortToken) {
          decoder.destroy();
        } else {
          const wasmModule = isWasmLoaded() ? getWasmModule() : null;
          if (r && wasmModule) {
            const bridge = new VideoTextureBridge(decoder, r, wasmModule);
            videoBridgeRef.current = bridge;

            try { bridge.importRingTextures(); } catch (err) {
              console.warn('[App] Failed to import ring textures:', err);
            }

            // Compute which video frames we need (one per camera frame) and
            // start the decode pump — feeds all samples in DTS order at
            // hardware decode speed, buffers outputs for instant retrieval.
            const camCount = cameraFrameCount || 134;
            const neededVidFrames = new Set<number>();
            for (let cam = 0; cam < camCount; cam++) {
              neededVidFrames.add(Math.min(
                Math.round(cam * (decoder.frameCount - 1) / Math.max(camCount - 1, 1)),
                decoder.frameCount - 1
              ));
            }
            decoder.startSequentialDecode(neededVidFrames);

            // Decode frame 0 via pump so video is visible before depth loading starts
            try {
              await decoder.decodeFrame(0);
              r.setVideoRingIndex(decoder.getLastDecodedRingIndex());
              r.setVideoQuadFrame(0);
            } catch (_) { /* will retry during drain */ }
          }
        }
      } catch (err) {
        console.warn('[App] Video decode failed:', err);
      }
    }

    // Phase 2: Load depth maps + sync video — one frame at a time.
    // Depth fetching/EXR parsing run concurrently in background.
    // The drain loop loads one depth frame per rAF tick AND awaits the
    // corresponding video decode before advancing, keeping frustum, points,
    // and video in lockstep. The video pump runs ahead at hardware decode
    // speed, so awaitFrame() resolves nearly instantly.
    const depthMaps = pipelineData.depth_maps;
    if (!depthMaps?.length || !r.loadDepthMap) return;

    const total = depthMaps.length;
    let loaded = 0;
    const updateProgress = () => {
      depthProgressRef.current = { loaded, total };
      if (depthProgressElRef.current) depthProgressElRef.current.textContent = `Loading depth: ${loaded}/${total}`;
    };
    depthProgressRef.current = { loaded: 0, total };
    updateProgress();

    // Queue of parsed depth maps ready for WASM ingestion
    const parsedQueue: Array<{ frameIdx: number; width: number; height: number; data: Float32Array }> = [];
    let fetchesDone = false;

    // Fetch + parse concurrently (async — doesn't block render)
    const CONCURRENCY = 2;
    let nextIdx = 0;
    const fetchWorker = async () => {
      while (nextIdx < depthMaps.length) {
        if (depthAbortRef.current !== abortToken) return;
        const idx = nextIdx++;
        const dm = depthMaps[idx];
        const match = dm.filename.match(/frame_(\d+)\.exr$/);
        if (!match) { loaded++; updateProgress(); continue; }
        const frameIdx = parseInt(match[1], 10);
        try {
          const resp = await fetch(dm.url);
          if (!resp.ok) { loaded++; updateProgress(); continue; }
          const buffer = await resp.arrayBuffer();
          if (depthAbortRef.current !== abortToken) return;
          const { width, height, data } = await parseExr(buffer);
          if (depthAbortRef.current !== abortToken) return;
          parsedQueue.push({ frameIdx, width, height, data });
        } catch (err) {
          console.warn(`[DepthLoader] Error ${dm.filename}:`, err);
          loaded++;
          updateProgress();
        }
      }
    };
    const fetchPromise = Promise.all(Array.from({ length: CONCURRENCY }, () => fetchWorker()))
      .then(() => { fetchesDone = true; });

    // Video decode helper — maps depth frameIdx to video frame and decodes.
    // With the pump running, decodeFrame() returns nearly instantly (frame
    // is already decoded and buffered).
    const decoder = videoDecoderRef.current;
    const camCount = cameraFrameCount || 134;
    const syncVideoForFrame = async (camFrame: number) => {
      if (!decoder || !videoBridgeRef.current) return;
      try {
        const vidFrame = Math.min(
          Math.round(camFrame * (decoder.frameCount - 1) / Math.max(camCount - 1, 1)),
          decoder.frameCount - 1
        );
        await decoder.decodeFrame(vidFrame);
        r.setVideoRingIndex(decoder.getLastDecodedRingIndex());
        r.setVideoQuadFrame(camFrame);
      } catch (err) {
        console.warn(`[VideoSync] Frame ${camFrame} decode failed:`, err);
      }
    };

    // Drain queue: load depth + decode video per frame, then yield to rAF
    await new Promise<void>((resolve) => {
      const drain = async () => {
        if (depthAbortRef.current !== abortToken) { resolve(); return; }
        if (parsedQueue.length > 0) {
          const item = parsedQueue.shift()!;

          // Decode video BEFORE loading depth so they appear in the same frame.
          // loadDepthMap advances the frustum immediately, so video must be ready first.
          await syncVideoForFrame(item.frameIdx);

          r.loadDepthMap(0, item.frameIdx, item.width, item.height, item.data);
          loaded++;
          updateProgress();
        }
        if (!fetchesDone || parsedQueue.length > 0) {
          requestAnimationFrame(drain);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(drain);
    });
    await fetchPromise;

    if (depthAbortRef.current === abortToken) {
      depthProgressRef.current = null;
      if (depthProgressElRef.current) depthProgressElRef.current.textContent = '';
      console.log(`[DepthLoader] Complete: ${loaded}/${total}`);
    }
  }, [activePipeline, gpuDevice, cleanupVideoResources]);

  // Load WASM module and initialize renderer
  React.useEffect(() => {
    // Capture canvas ref at the start of the effect for cleanup
    const canvas = canvasRef.current;
    
    // Use a flag to prevent double initialization in StrictMode
    let isCleanedUp = false;
    let animationId: number | null = null;
    let rendererInstance: any = null;
    let resizeObserver: ResizeObserver | null = null;
    
    // Store event handlers for cleanup
    const eventHandlers: { 
      canvas?: { [key: string]: EventListener }, 
      window?: { [key: string]: EventListener },
      document?: { [key: string]: EventListener }
    } = {};

    const initRenderer = async () => {
      if (!canvas || isCleanedUp) return;

      try {
        // Use the captured canvas from effect start
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;

        // Set actual canvas size (for WebGPU)
        canvas.width = width;
        canvas.height = height;

        console.log(`📐 Canvas size: ${width}x${height}`);

        // Initialize WebGPU and pass to WASM module
        console.log('🎮 Initializing WebGPU for Dawn...');
        if (!navigator.gpu) {
          throw new Error('WebGPU not supported in this browser');
        }

        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter) {
          throw new Error('Failed to get WebGPU adapter');
        }

        // Request timestamp-query feature when adapter supports it (INST-02).
        // This enables GPU pass timing in scFrameOrchestrator without affecting
        // browsers that don't support the feature.
        const requiredFeatures: GPUFeatureName[] = [];
        if (adapter.features.has('timestamp-query')) {
          requiredFeatures.push('timestamp-query');
          console.log('[WebGPU] timestamp-query feature requested');
        }
        const device = await adapter.requestDevice({
          requiredFeatures,
          requiredLimits: {
            maxBufferSize: adapter.limits.maxBufferSize,
            maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
          },
        });
        if (!device) {
          throw new Error('Failed to get WebGPU device');
        }
        setGpuDevice(device);

        // MSAA device detection: auto-disable on mobile/low-end GPUs (TOGL-03)
        let defaultMSAAEnabled = true;
        try {
            const adapterInfo = adapter.info;
            const description = adapterInfo?.description || '';
            const architecture = adapterInfo?.architecture || '';

            // Mobile GPU detection heuristics
            const mobileGPUPattern = /Adreno|Mali|PowerVR|Apple GPU/i;
            const lowEndGPUPattern = /Intel HD Graphics|Intel UHD 6[0-2]/i;

            const isMobileGPU = mobileGPUPattern.test(description) || mobileGPUPattern.test(architecture);
            const isLowEndGPU = lowEndGPUPattern.test(description) || lowEndGPUPattern.test(architecture);

            // Also check device texture limits as a low-end proxy
            const maxTexDim = device.limits.maxTextureDimension2D;

            defaultMSAAEnabled = !isMobileGPU && !isLowEndGPU && maxTexDim >= 8192;

            console.log(`[MSAA] GPU: "${description}", arch: "${architecture}", ` +
                        `mobile=${isMobileGPU}, lowEnd=${isLowEndGPU}, ` +
                        `maxTex=${maxTexDim}, defaultMSAA=${defaultMSAAEnabled}`);
        } catch (e) {
            // GPUAdapterInfo not available -- default to MSAA enabled
            console.log('[MSAA] GPUAdapterInfo not available, defaulting to MSAA enabled');
            defaultMSAAEnabled = true;
        }

        // Check if cleaned up during async operations
        if (isCleanedUp) {
          console.log('🛑 Cleanup occurred during initialization, aborting');
          return;
        }

        // Load WASM module with WebGPU device
        console.log('🚀 Loading Tarsius WASM module with WebGPU device...');
        const module = await loadTarsiusWasm(device);
        
        // Check if cleaned up during async operations
        if (isCleanedUp) {
          console.log('🛑 Cleanup occurred during WASM load, aborting');
          return;
        }
        
        console.log('✅ WASM module loaded successfully');
        wasmModuleRef.current = module;
        console.log('📦 Available exports:', Object.keys(module).filter(k => typeof module[k] === 'function'));

        // Create renderer instance
        if (module.ScTarsiusWebRenderer) {
          console.log('🎨 Creating renderer instance...');
          rendererInstance = new module.ScTarsiusWebRenderer();
          
          // Initialize the renderer with the canvas
          // The C++ code expects a canvas selector (for Emscripten to find it)
          const success = rendererInstance.initializeRenderer('#canvas', width, height);
          
          if (success) {
            rendererInstanceRef.current = rendererInstance;
            setRenderer(rendererInstance);
            setIsInitialized(true);
            (window as any)._scRenderer = rendererInstance;  // DEBUG: expose for puppeteer testing
            console.log('✅ Renderer initialized successfully!');

            // Enable offscreen mode: C++ renders to scene texture, skips surface present
            // When noGlass is set, render directly to surface (no offscreen indirection)
            if (!noGlass) {
              rendererInstance.setOffscreenMode(true);
            }

            // Extract scene texture for glass integration.
            // The scene texture is a single (non-ring-buffered) GPU texture whose
            // handle is stable across frames, so a one-time extraction is correct.
            try {
              let tex: GPUTexture | undefined;

              // Preferred path: C++ resolves handle to JS GPUTexture internally
              if (typeof rendererInstance.getSceneTextureJS === 'function') {
                tex = rendererInstance.getSceneTextureJS() as GPUTexture | undefined;
                console.log('[Glass] getSceneTextureJS:', tex,
                  tex ? `${tex.width}x${tex.height} ${tex.format}` : 'null');
              }

              // Fallback: manual handle lookup via emdawnwebgpu table
              if (!tex) {
                const sceneHandle = rendererInstance.getSceneTextureHandle();
                console.log('[Glass] fallback sceneHandle:', sceneHandle,
                  'WebGPU:', !!module.WebGPU,
                  'getJsObject:', typeof module.WebGPU?.getJsObject);
                if (sceneHandle && module.WebGPU) {
                  const jsObjects = module.WebGPU.Internals?.jsObjects;
                  console.log('[Glass] handle in jsObjects:', jsObjects ? (sceneHandle in jsObjects) : 'N/A',
                    'direct lookup type:', jsObjects?.[sceneHandle]?.constructor?.name);
                  const getJsObject = module.WebGPU.getJsObject || ((ptr: number) => module.WebGPU.Internals?.jsObjects?.[ptr]);
                  tex = getJsObject(sceneHandle) as GPUTexture | undefined;
                  console.log('[Glass] resolved:', tex,
                    tex ? `${tex.width}x${tex.height} ${tex.format}` : 'null');
                }
              }

              if (tex) {
                setSceneTexture(tex);
                console.log('[Glass] Scene texture set for glass integration');
              } else {
                console.warn('[Glass] Scene texture extraction failed — glass will show default background');
              }
            } catch (e) {
              console.warn('[Glass] Failed to extract scene texture:', e);
            }

            // Auto-disable MSAA on detected low-end/mobile devices
            if (!defaultMSAAEnabled) {
                rendererInstance.setMSAAEnabled(false);
                console.log('[MSAA] Auto-disabled for detected low-end/mobile device');
            }

            // Track mouse state for global capture
            let isMouseDown = false;
            
            // Add input event listeners to canvas
            const handleMouseDown = (e: MouseEvent) => {
              if (!rendererInstance) return;

              // Exit pointer lock if it was requested (we don't want it)
              if (document.pointerLockElement) {
                document.exitPointerLock();
              }

              isMouseDown = true;

              const rect = canvas.getBoundingClientRect();
              const x = Math.floor(e.clientX - rect.left);
              const y = Math.floor(e.clientY - rect.top);
              rendererInstance.onMouseDown(x, y, e.button, e.shiftKey, e.ctrlKey);
              
              // Capture mouse globally during drag
              document.addEventListener('mousemove', handleMouseMoveGlobal);
              document.addEventListener('mouseup', handleMouseUpGlobal);
              
              e.preventDefault();
              e.stopPropagation(); // Stop event from reaching Emscripten's handlers
            };
            
            const handleMouseUpGlobal = (e: MouseEvent) => {
              if (!rendererInstance) return;

              isMouseDown = false;

              const rect = canvas.getBoundingClientRect();
              const x = Math.floor(e.clientX - rect.left);
              const y = Math.floor(e.clientY - rect.top);
              rendererInstance.onMouseUp(x, y, e.button, e.shiftKey, e.ctrlKey);
              
              // Release global capture
              document.removeEventListener('mousemove', handleMouseMoveGlobal);
              document.removeEventListener('mouseup', handleMouseUpGlobal);
              
              e.preventDefault();
            };
            
            const handleMouseMoveGlobal = (e: MouseEvent) => {
              if (!rendererInstance || !isMouseDown) return;

              const rect = canvas.getBoundingClientRect();
              const x = Math.floor(e.clientX - rect.left);
              const y = Math.floor(e.clientY - rect.top);
              const leftDown = (e.buttons & 1) !== 0;
              const rightDown = (e.buttons & 2) !== 0;

              rendererInstance.onMouseMove(x, y, e.shiftKey, e.ctrlKey, leftDown, rightDown);
              e.preventDefault();
            };
            
            const handleWheel = (e: WheelEvent) => {
              if (!rendererInstance) return;
              const rect = canvas.getBoundingClientRect();
              const x = Math.floor(e.clientX - rect.left);
              const y = Math.floor(e.clientY - rect.top);
              // Normalize wheel delta (typically -100 to +100 per notch)
              const delta = Math.sign(e.deltaY) * -30;// -120;
              rendererInstance.onMouseWheel(x, y, delta);
              e.preventDefault();
            };
            
            const handleMouseLeave = () => {
              if (!rendererInstance) return;
              rendererInstance.onMouseLeave();
            };
            
            const handleKeyDown = (e: KeyboardEvent) => {
              if (!rendererInstance) return;
              rendererInstance.onKeyDown(e.keyCode, e.shiftKey, e.ctrlKey);
              // Don't prevent default for all keys (allow browser shortcuts)
              // Only prevent for keys we handle
              if (e.keyCode >= 37 && e.keyCode <= 40) { // Arrow keys
                e.preventDefault();
              }
            };
            
            const handleKeyUp = (e: KeyboardEvent) => {
              if (!rendererInstance) return;
              rendererInstance.onKeyUp(e.keyCode, e.shiftKey, e.ctrlKey);
            };
            
            // Prevent browser context menu on right-click
            const handleContextMenu = (e: MouseEvent) => {
              e.preventDefault();
            };

            // Store handlers for cleanup
            eventHandlers.canvas = {
              mousedown: handleMouseDown as EventListener,
              wheel: handleWheel as EventListener,
              mouseleave: handleMouseLeave as EventListener,
              contextmenu: handleContextMenu as EventListener,
            };
            eventHandlers.window = {
              keydown: handleKeyDown as EventListener,
              keyup: handleKeyUp as EventListener,
            };
            
            // Add event listeners (mouseup and mousemove are added/removed dynamically during drag)
            canvas.addEventListener('mousedown', eventHandlers.canvas.mousedown);
            canvas.addEventListener('wheel', eventHandlers.canvas.wheel, { passive: false });
            canvas.addEventListener('mouseleave', eventHandlers.canvas.mouseleave);
            canvas.addEventListener('contextmenu', eventHandlers.canvas.contextmenu);
            window.addEventListener('keydown', eventHandlers.window.keydown);
            window.addEventListener('keyup', eventHandlers.window.keyup);
            
            // Make canvas focusable for keyboard events
            canvas.tabIndex = 1;
            canvas.focus();
            
            // Prevent pointer lock (Emscripten tries to enable it by default)
            const blockPointerLock = () => {
              if (document.pointerLockElement === canvas) {
                document.exitPointerLock();
              }
            };
            document.addEventListener('pointerlockchange', blockPointerLock);
            
            // Store for cleanup
            eventHandlers.document = { pointerlockchange: blockPointerLock as EventListener };
            
            console.log('✅ Input handlers attached');
            
            // Render loop — C++ renderer only. Glass rendering is handled by SceneGlassProvider's own RAF.
            let frameCount = 0;
            // Frame interval diagnostics (?diag=1)
            let diagLastFrame = 0;
            let diagIntervals: number[] = [];
            const renderLoop = () => {
              if (!isCleanedUp && rendererInstance) {
                const now = performance.now();
                frameCount++;

                // Diagnostics: measure actual frame intervals
                if (diagMode) {
                  if (diagLastFrame > 0) {
                    diagIntervals.push(now - diagLastFrame);
                  }
                  diagLastFrame = now;
                  if (frameCount % 60 === 0 && diagIntervals.length > 0) {
                    const avg = diagIntervals.reduce((a, b) => a + b, 0) / diagIntervals.length;
                    const min = Math.min(...diagIntervals);
                    const max = Math.max(...diagIntervals);
                    console.log(`[DIAG] rAF intervals (last 60): avg=${avg.toFixed(1)}ms min=${min.toFixed(1)}ms max=${max.toFixed(1)}ms → ${(1000 / avg).toFixed(1)} rAF/s | glass=${noGlass ? 'OFF' : 'ON'}`);
                    diagIntervals = [];
                  }
                }

                rendererInstance.setRafTimestamp(now);
                rendererInstance.renderFrame();

                // Update copyright text from renderer (throttled to avoid per-frame React re-renders)
                if (frameCount % 60 === 0) {
                  try {
                    const copyrights = rendererInstance.getAggregatedCopyrights();
                    if (copyrights !== copyrightText) {
                      setCopyrightText(copyrights);
                    }
                  } catch (e) {
                    // Method might not be available yet, ignore
                  }
                }

                animationId = requestAnimationFrame(renderLoop);
              }
            };
            renderLoop();

            // RSZ-01, RSZ-02, RSZ-03: Observe canvas for size changes
            // ResizeObserver fires at most once per animation frame (no debounce needed).
            // The C++ resize chain (setViewport -> scNewRenderer::Resize -> scFrameOrchestrator::OnResize)
            // handles all GPU resource recreation (depth, MSAA, scene texture, bloom mips, terrain targets).
            resizeObserver = new ResizeObserver((entries) => {
              const entry = entries[0];
              if (!entry || !rendererInstance) return;

              // Use contentBoxSize for CSS pixel dimensions (matching init convention -- no DPR scaling)
              const width = Math.round(entry.contentBoxSize[0].inlineSize);
              const height = Math.round(entry.contentBoxSize[0].blockSize);

              // Guard: skip zero dimensions (browser minimized) to avoid GPU assertion failures
              if (width === 0 || height === 0) return;

              // Guard: skip if dimensions haven't actually changed (initial observation fires immediately)
              if (width === canvas.width && height === canvas.height) return;

              // RSZ-02: Update canvas internal resolution to match CSS layout
              canvas.width = width;
              canvas.height = height;

              // RSZ-01 + RSZ-03: Trigger C++ resize chain -- recreates all screen-dependent GPU resources
              rendererInstance.setViewport(width, height);

              // Re-extract scene texture (destroyed and recreated by OnResize)
              if (!noGlass) {
                try {
                  const tex = rendererInstance.getSceneTextureJS();
                  if (tex) setSceneTexture(tex);
                } catch (e) {
                  console.warn('[Resize] Failed to re-extract scene texture:', e);
                }
              }
            });
            resizeObserver.observe(canvas);
          } else {
            throw new Error('Failed to initialize renderer - check console for details');
          }
        } else {
          throw new Error('ScTarsiusWebRenderer not found in WASM module');
        }
      } catch (error) {
        console.error('❌ Failed to initialize renderer:', error);
        setLoadError(error instanceof Error ? error.message : 'Unknown error');
      }
    };

    initRenderer();
    
    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up renderer...');
      isCleanedUp = true;

      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      
      // Remove event listeners (canvas was captured at effect start)
      if (canvas && eventHandlers.canvas) {
        canvas.removeEventListener('mousedown', eventHandlers.canvas.mousedown);
        canvas.removeEventListener('wheel', eventHandlers.canvas.wheel);
        canvas.removeEventListener('mouseleave', eventHandlers.canvas.mouseleave);
        canvas.removeEventListener('contextmenu', eventHandlers.canvas.contextmenu);
      }
      if (eventHandlers.window) {
        window.removeEventListener('keydown', eventHandlers.window.keydown);
        window.removeEventListener('keyup', eventHandlers.window.keyup);
      }
      if (eventHandlers.document) {
        document.removeEventListener('pointerlockchange', eventHandlers.document.pointerlockchange);
      }
      // Note: global mousemove/mouseup are cleaned up in handleMouseUpGlobal
      
      // Clean up video resources before renderer
      if (videoBridgeRef.current) { videoBridgeRef.current.destroy(); videoBridgeRef.current = null; }
      if (videoDecoderRef.current) { videoDecoderRef.current.destroy(); videoDecoderRef.current = null; }

      if (rendererInstance) {
        try {
          rendererInstance.destroy();
          rendererInstance.delete();
        } catch (e) {
          console.error('Error during renderer cleanup:', e);
        }
        rendererInstance = null;
      }
    };
  }, []);

  // Backtick key toggles tile metrics overlay
  React.useEffect(() => {
    const handleOverlayToggle = (e: KeyboardEvent) => {
      if (e.key === '`') {
        setShowMetricsOverlay(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleOverlayToggle);
    return () => window.removeEventListener('keydown', handleOverlayToggle);
  }, []);

  // 'P' key toggles video projection mode (quad <-> projection)
  React.useEffect(() => {
    const handleProjectionToggle = (e: KeyboardEvent) => {
      if (e.key === 'p' || e.key === 'P') {
        const r = (window as any)._scRenderer;
        if (r && typeof r.setProjectionMode === 'function') {
          const current = r.getProjectionMode();
          r.setProjectionMode(!current);
          console.log(`[VideoProjection] ${!current ? 'PROJECTION' : 'QUAD'} mode`);
        }
      }
    };
    window.addEventListener('keydown', handleProjectionToggle);
    return () => window.removeEventListener('keydown', handleProjectionToggle);
  }, []);

  // 'W' key toggles wireframe mode for terrain tessellation debug
  React.useEffect(() => {
    let wireframeOn = false;
    const handleWireframeToggle = (e: KeyboardEvent) => {
      if (e.key === 'w' || e.key === 'W') {
        const r = (window as any)._scRenderer;
        if (r && typeof r.setWireframeMode === 'function') {
          wireframeOn = !wireframeOn;
          r.setWireframeMode(wireframeOn);
          console.log(`[Wireframe] ${wireframeOn ? 'ON' : 'OFF'}`);
        }
      }
    };
    window.addEventListener('keydown', handleWireframeToggle);
    return () => window.removeEventListener('keydown', handleWireframeToggle);
  }, []);

  const handleLocationSelect = useCallback(async (lon: number, lat: number, name: string) => {
    if (!renderer) return;
    console.log(`Flying to ${name}: ${lat}, ${lon}`);

    // Query Google Elevation API for ground elevation at this location
    let alt = 1000.0; // fallback
    try {
      const resp = await fetch(`https://tarsiusweb.com/api/elevation/${lat}/${lon}`);
      if (resp.ok) {
        const data = await resp.json();
        const elev = data?.results?.[0]?.elevation;
        if (elev != null) {
          // Fly to ground elevation + offset for a good overhead view
          alt = elev + 1000.0;
        }
      }
    } catch (e) {
      console.warn('Elevation lookup failed, using default altitude', e);
    }

    renderer.flyToWithOrientation(lon, lat, alt, 0.0, -90.0);
  }, [renderer]);

  // handleAddData is reserved for future use
  // const handleAddData = useCallback((type: 'photo' | 'scan' | 'measurement') => {
  //   console.log('Add data requested:', type);
  // }, []);

  // Show video processing page if in that mode
  if (viewMode === 'video-processing') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ height: '100vh', overflow: 'auto', background: '#f5f5f5' }}>
          <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 1000 }}>
            <Button
              variant="contained"
              startIcon={<View3DIcon />}
              onClick={() => setViewMode('renderer')}
            >
              Back to Renderer
            </Button>
          </Box>
          <VideoProcessingPage />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <TemporalProvider>
    {/* Activate raster tile system inside TemporalProvider so it can consume TemporalContext.
        Gated on isInitialized so window._scRenderer exists when the hook mounts. */}
    {isInitialized && <RasterLayerManagerActivator />}
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
        background: '#000'
      }}>
        {/* Tarsius canvas - when noGlass, render directly visible; otherwise invisible event capture layer */}
        <canvas
          id="canvas"
          ref={canvasRef}
          onClick={(e: React.MouseEvent<HTMLCanvasElement>) => {
            // HAZ-05: Hit test markers at click position
            const r = (window as any)._scRenderer;
            if (r && typeof r.getMarkerAtScreen === 'function') {
              try {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = Math.floor(e.clientX - rect.left);
                const y = Math.floor(e.clientY - rect.top);
                const hitResult = r.getMarkerAtScreen(x, y);
                if (hitResult && hitResult.hit) {
                  setTooltipData({
                    layerId: hitResult.layerId,
                    screenX: e.clientX,
                    screenY: e.clientY,
                    lon: hitResult.lon,
                    lat: hitResult.lat,
                    depthOrAlt: hitResult.depthOrAlt,
                    value: hitResult.value,
                    label: hitResult.label || undefined,
                  });
                } else {
                  setTooltipData(null);
                }
              } catch (_) {
                // getMarkerAtScreen not yet available (WASM not rebuilt yet); ignore
              }
            } else {
              // Dismiss tooltip on canvas click when no renderer method available
              setTooltipData(null);
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: noGlass ? 1 : 0,
            zIndex: noGlass ? 0 : 2,
          }}
        />

        {/* Loading State - visible during init (before GlassProvider is ready) */}
        {!isInitialized && !loadError && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                border: '3px solid rgba(255, 255, 255, 0.1)',
                borderTop: '3px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
                margin: '0 auto 16px',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontWeight: 500,
              }}
            >
              Loading...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {loadError && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              width: '90%',
              maxWidth: 500,
            }}
          >
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(211, 47, 47, 0.3)',
                color: 'white',
              }}
            >
              <Typography variant="h6">Failed to load</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {loadError}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Glass-enhanced UI layer: scene texture + glass effects + UI overlays */}
        {gpuDevice && !noGlass && (
          <SceneGlassProvider device={gpuDevice} sceneTexture={sceneTexture}>
            {/* Tile Metrics Overlay (toggled by backtick key) */}
            <TileMetricsOverlay renderer={renderer} visible={showMetricsOverlay} />

            {/* Floating Top-Left Logo/Title */}
            <Box
              sx={{
                position: 'absolute',
                top: 24,
                left: 24,
                zIndex: 10,
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  letterSpacing: -0.5,
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                }}
              >
                Tarsius
              </Typography>
            </Box>

            {/* Location Search Bar - Top Center */}
            {isInitialized && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                }}
              >
                <LocationSearchBar onLocationSelect={handleLocationSelect} />
              </Box>
            )}

            {/* Hamburger Menu Button - Top Right (symmetric with Tarsius title) */}
            <Box sx={{ position: 'absolute', top: 24, right: 24, zIndex: 12 }}>
              <IconButton
                onClick={() => setMenuOpen(prev => !prev)}
                sx={{
                  color: 'white',
                  padding: 0,
                  '&:hover': { backgroundColor: 'transparent', opacity: 0.7 },
                }}
                disableRipple
                title="Menu"
              >
                {menuOpen ? <CloseIcon sx={{ fontSize: 28 }} /> : <MenuIcon sx={{ fontSize: 28 }} />}
              </IconButton>
            </Box>

            {/* Animated Settings Menu - slides in from right */}
            <Box
              sx={{
                position: 'absolute',
                top: 60,
                right: 20,
                zIndex: 11,
                transform: menuOpen ? 'translateX(0)' : 'translateX(calc(100% + 40px))',
                opacity: menuOpen ? 1 : 0,
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: menuOpen ? 'auto' : 'none',
              }}
            >
              <GlassPanel
                style={{ padding: '20px', width: 300 }}
              >
                {/* Time of Day */}
                {renderer && (
                  <Box sx={{ mb: 2 }}>
                    <TimeOfDayControl renderer={renderer} />
                  </Box>
                )}

                {/* Divider */}
                <Box sx={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.12)', my: 1.5 }} />

                {/* Data Layers — earthquake + wildfire toggles */}
                <LayerPanel
                  layers={layers}
                  onToggle={onToggle}
                  onOpacityChange={onOpacityChange}
                />

                {/* Divider */}
                <Box sx={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.12)', my: 1.5 }} />

                {/* Time Slider — temporal navigation for time-aware layers */}
                <TimeSlider />

                {/* Divider */}
                <Box sx={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.12)', my: 1.5 }} />

                {/* Raster Upload */}
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.5, fontSize: '0.65rem' }}>
                  Raster Upload
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <RasterUploadPanel />
                </Box>

                {/* Divider */}
                <Box sx={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.12)', my: 1.5 }} />

                {/* Pipeline Tests */}
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.5, fontSize: '0.65rem' }}>
                  Pipeline Tests
                </Typography>
                {availablePipelines.length === 0 && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
                    No test set available
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                  {availablePipelines.map((name) => {
                    const isActive = activePipeline === name;
                    const isLoading = loadingPipeline === name;
                    return (
                      <Button
                        key={name}
                        onClick={() => handlePipelineClick(name)}
                        size="small"
                        sx={{
                          color: 'white',
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          backgroundColor: isActive ? 'rgba(100, 200, 255, 0.2)' : 'transparent',
                          borderLeft: isActive ? '3px solid rgba(100, 200, 255, 0.8)' : '3px solid transparent',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                          pl: 1.5,
                          minHeight: 32,
                        }}
                        startIcon={
                          isLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> :
                          isActive ? <StopIcon sx={{ fontSize: 14 }} /> : undefined
                        }
                      >
                        {name}
                      </Button>
                    );
                  })}
                  <Typography
                    ref={depthProgressElRef}
                    variant="caption"
                    component="span"
                    sx={{ color: 'rgba(255,255,255,0.5)', pl: 2, fontFamily: 'monospace', fontSize: '0.7rem', display: 'block' }}
                  />
                </Box>

                {/* Divider */}
                <Box sx={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.12)', my: 1.5 }} />

                {/* Poly Sets */}
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.5, fontSize: '0.65rem' }}>
                  Poly Sets
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      if (renderer) {
                        renderer.loadRecordedPointPolys(window.location.origin + '/data/poly-sets/mckenna-mout/manifest.json');
                        renderer.flyToWithOrientation(-84.80573307173212, 32.370321682535916, 137.20690207369626, 82.0, -17.0);
                        setMenuOpen(false);
                      }
                    }}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      borderLeft: '3px solid transparent',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      pl: 1.5,
                      minHeight: 32,
                    }}
                    startIcon={<View3DIcon sx={{ fontSize: 14 }} />}
                  >
                    McKennaMOUT
                  </Button>
                </Box>

                {/* Divider */}
                <Box sx={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.12)', my: 1.5 }} />

                {/* Video Processing Link */}
                <Box
                  onClick={() => { setViewMode('video-processing'); setMenuOpen(false); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    padding: '10px 8px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  <VideoIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 22 }} />
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}
                  >
                    Video Processing
                  </Typography>
                </Box>
              </GlassPanel>
            </Box>

            {/* Hazard Marker Tooltip — shown on marker click (HAZ-05) */}
            {tooltipData && (
              <HazardTooltip
                data={tooltipData}
                onDismiss={() => setTooltipData(null)}
              />
            )}

            {/* Floating Camera Coordinates - Bottom Right */}
            {isInitialized && <CameraCoordinates rendererRef={rendererInstanceRef} />}

            {/* FPS Counter - Bottom Left */}
            {isInitialized && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: copyrightText ? 48 : 20,
                  left: 20,
                  zIndex: 10,
                }}
              >
                <FpsCounter rendererRef={rendererInstanceRef} />
              </Box>
            )}

            {/* Copyright Text - Bottom Left */}
            {isInitialized && copyrightText && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: 20,
                  zIndex: 10,
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.75rem',
                  pointerEvents: 'none',
                  maxWidth: 'calc(100vw - 40px)',
                }}
              >
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {copyrightText}
                </Typography>
              </Box>
            )}
          </SceneGlassProvider>
        )}
      </Box>
    </ThemeProvider>
    </TemporalProvider>
  );
}

export default App;